import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    CommonTenantRequest,
    UpsertTenantAdminAndCreatorRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/tenantAdminAndCreator.api.ts';
import type {
    TenantAdminAndCreatorItem,
    TenantAdminAndCreatorSearchResponse,
    TenantAdminAndCreatorUsersSearchResponse,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/tenantAdminAndCreator.api.ts';
import {
    createApiTenantAdminAndCreator,
    deleteApiTenantAdminAndCreator,
    getApiTenantAdminAndCreator,
    getApiTenantAdminAndCreatorUsers,
    updateApiTenantAdminAndCreator,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/api/tenantAdminAndCreator.api.ts';
import { extractError } from '@utils/slice.ts';

interface TenantCreatorError {
    message?: string;
    [key: string]: any;
}

interface TenantCreatorState {
    error: TenantCreatorError | null;
    tenantCreatorsData: TenantAdminAndCreatorSearchResponse | null;
    tenantCreatorUsersData: TenantAdminAndCreatorUsersSearchResponse | null;

    isGetTenantCreatorsPending: boolean;
    isCreateTenantCreatorPending: boolean;
    isUpdateTenantCreatorPending: boolean;
    isDeleteTenantCreatorPending: boolean;
    isGetTenantCreatorUsersPending: boolean;
}

const initialState: TenantCreatorState = {
    error: null,
    tenantCreatorsData: null,
    tenantCreatorUsersData: null,

    isGetTenantCreatorsPending: false,
    isCreateTenantCreatorPending: false,
    isUpdateTenantCreatorPending: false,
    isDeleteTenantCreatorPending: false,
    isGetTenantCreatorUsersPending: false,
};

export const getTenantCreators = createAsyncThunk<
    TenantAdminAndCreatorSearchResponse,
    { tenantAlias: string; payload: CommonTenantRequest },
    { rejectValue: TenantCreatorError }
>('tenantCreatorRoles/getTenantCreators', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiTenantAdminAndCreator(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createTenantCreator = createAsyncThunk<
    TenantAdminAndCreatorItem,
    { tenantAlias: string; payload: UpsertTenantAdminAndCreatorRequest },
    { rejectValue: TenantCreatorError }
>('tenantCreatorRoles/createTenantCreator', ({ tenantAlias, payload }, { rejectWithValue }) =>
    createApiTenantAdminAndCreator(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateTenantCreator = createAsyncThunk<
    TenantAdminAndCreatorItem,
    { tenantAlias: string; accessId: number; payload: UpsertTenantAdminAndCreatorRequest },
    { rejectValue: TenantCreatorError }
>('tenantCreatorRoles/updateTenantCreator', ({ tenantAlias, accessId, payload }, { rejectWithValue }) =>
    updateApiTenantAdminAndCreator(tenantAlias, accessId, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteTenantCreator = createAsyncThunk<
    void,
    { tenantAlias: string; accessId: number },
    { rejectValue: TenantCreatorError }
>('tenantCreatorRoles/deleteTenantCreator', ({ tenantAlias, accessId }, { rejectWithValue }) =>
    deleteApiTenantAdminAndCreator(tenantAlias, accessId)
        .then(() => undefined)
        .catch((err) => rejectWithValue(err)),
);

export const getTenantCreatorUsers = createAsyncThunk<
    TenantAdminAndCreatorUsersSearchResponse,
    { tenantAlias: string; payload: CommonTenantRequest },
    { rejectValue: TenantCreatorError }
>('tenantCreatorRoles/getTenantCreatorUsers', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiTenantAdminAndCreatorUsers(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const tenantCreatorSlice = createSlice({
    name: 'tenantCreatorRoles',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getTenantCreators.pending, (state) => {
            state.isGetTenantCreatorsPending = true;
            state.error = null;
        });
        builder.addCase(getTenantCreators.fulfilled, (state, action) => {
            state.isGetTenantCreatorsPending = false;
            state.tenantCreatorsData = action.payload;
        });
        builder.addCase(getTenantCreators.rejected, (state, action) => {
            state.isGetTenantCreatorsPending = false;
            state.error = extractError(action);
        });

        builder.addCase(createTenantCreator.pending, (state) => {
            state.isCreateTenantCreatorPending = true;
            state.error = null;
        });
        builder.addCase(createTenantCreator.fulfilled, (state) => {
            state.isCreateTenantCreatorPending = false;
        });
        builder.addCase(createTenantCreator.rejected, (state, action) => {
            state.isCreateTenantCreatorPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateTenantCreator.pending, (state) => {
            state.isUpdateTenantCreatorPending = true;
            state.error = null;
        });
        builder.addCase(updateTenantCreator.fulfilled, (state) => {
            state.isUpdateTenantCreatorPending = false;
        });
        builder.addCase(updateTenantCreator.rejected, (state, action) => {
            state.isUpdateTenantCreatorPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteTenantCreator.pending, (state) => {
            state.isDeleteTenantCreatorPending = true;
            state.error = null;
        });
        builder.addCase(deleteTenantCreator.fulfilled, (state) => {
            state.isDeleteTenantCreatorPending = false;
        });
        builder.addCase(deleteTenantCreator.rejected, (state, action) => {
            state.isDeleteTenantCreatorPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getTenantCreatorUsers.pending, (state) => {
            state.isGetTenantCreatorUsersPending = true;
            state.error = null;
        });
        builder.addCase(getTenantCreatorUsers.fulfilled, (state, action) => {
            state.isGetTenantCreatorUsersPending = false;
            state.tenantCreatorUsersData = action.payload;
        });
        builder.addCase(getTenantCreatorUsers.rejected, (state, action) => {
            state.isGetTenantCreatorUsersPending = false;
            state.error = extractError(action);
        });
    },
});

export default tenantCreatorSlice.reducer;
